import { useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, X, Image as ImageIcon, Loader2, CheckCircle, AlertCircle, GripVertical } from 'lucide-react';

import { API_URL } from '../config/api';

// Configuration
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const MAX_IMAGES = 10;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const COMPRESSION_QUALITY = 0.85;
const MAX_DIMENSION = 1920;

/**
 * Compresse une image côté client avant upload
 */
const compressImage = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        
        // Redimensionner si nécessaire
        if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
          if (width > height) {
            height = Math.round(height * (MAX_DIMENSION / width));
            width = MAX_DIMENSION;
          } else {
            width = Math.round(width * (MAX_DIMENSION / height));
            height = MAX_DIMENSION;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve({
                file: compressedFile,
                preview: canvas.toDataURL('image/jpeg', COMPRESSION_QUALITY),
                originalSize: file.size,
                compressedSize: blob.size,
                dimensions: { width, height }
              });
            } else {
              reject(new Error('Compression failed'));
            }
          },
          'image/jpeg',
          COMPRESSION_QUALITY
        );
      };
      img.onerror = () => reject(new Error('Image load failed'));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error('File read failed'));
    reader.readAsDataURL(file);
  });
};

/**
 * Composant ImageUploader avec Drag & Drop
 */
export default function ImageUploader({ 
  images = [], 
  onChange, 
  maxImages = MAX_IMAGES,
  folder = 'products'
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const fileInputRef = useRef(null);
  const dragCounter = useRef(0);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragIn = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  }, []);

  const handleDragOut = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const processFiles = async (files) => {
    const validFiles = Array.from(files).filter(file => {
      if (!ALLOWED_TYPES.includes(file.type)) {
        console.warn(`Type non supporté: ${file.type}`);
        return false;
      }
      if (file.size > MAX_FILE_SIZE) {
        console.warn(`Fichier trop volumineux: ${file.name}`);
        return false;
      }
      return true;
    });

    const remainingSlots = maxImages - images.length;
    const filesToProcess = validFiles.slice(0, remainingSlots);

    if (filesToProcess.length === 0) return;

    setUploading(true);
    const newImages = [...images];
    const token = localStorage.getItem('client_token');

    for (let i = 0; i < filesToProcess.length; i++) {
      const file = filesToProcess[i];
      const tempId = `temp-${Date.now()}-${i}`;
      
      try {
        // Update progress
        setUploadProgress(prev => ({
          ...prev,
          [tempId]: { status: 'compressing', progress: 0 }
        }));

        // Compress image client-side
        const compressed = await compressImage(file);
        
        setUploadProgress(prev => ({
          ...prev,
          [tempId]: { status: 'uploading', progress: 50 }
        }));

        // Upload to server
        const formData = new FormData();
        formData.append('file', compressed.file);
        formData.append('folder', folder);

        const response = await fetch(`${API_URL}/api/upload/image`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (response.ok) {
          const result = await response.json();
          
          newImages.push({
            id: result.filename,
            url: result.url,
            thumbnail_url: result.thumbnail_url,
            is_primary: newImages.length === 0,
            alt_text: file.name.replace(/\.[^/.]+$/, ''),
            originalSize: compressed.originalSize,
            compressedSize: compressed.compressedSize,
            dimensions: result.dimensions
          });

          setUploadProgress(prev => ({
            ...prev,
            [tempId]: { status: 'done', progress: 100 }
          }));
        } else {
          const error = await response.json();
          setUploadProgress(prev => ({
            ...prev,
            [tempId]: { status: 'error', error: error.detail || 'Erreur upload' }
          }));
        }
      } catch (error) {
        console.error('Upload error:', error);
        setUploadProgress(prev => ({
          ...prev,
          [tempId]: { status: 'error', error: error.message }
        }));
      }
    }

    onChange(newImages);
    setUploading(false);
    
    // Clear progress after delay
    setTimeout(() => setUploadProgress({}), 2000);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFiles(e.dataTransfer.files);
      e.dataTransfer.clearData();
    }
  }, [images, maxImages]);

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      processFiles(e.target.files);
      e.target.value = '';
    }
  };

  const handleRemove = async (index) => {
    const image = images[index];
    const token = localStorage.getItem('client_token');

    // Try to delete from server
    if (image.id && !image.id.startsWith('temp-')) {
      try {
        await fetch(`${API_URL}/api/upload/image/${image.id}?folder=${folder}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } catch (e) {
        console.warn('Delete failed:', e);
      }
    }

    const newImages = images.filter((_, i) => i !== index);
    // Ensure first image is primary
    if (newImages.length > 0 && !newImages.some(img => img.is_primary)) {
      newImages[0].is_primary = true;
    }
    onChange(newImages);
  };

  const handleSetPrimary = (index) => {
    const newImages = images.map((img, i) => ({
      ...img,
      is_primary: i === index
    }));
    onChange(newImages);
  };

  const handleReorder = (fromIndex, toIndex) => {
    const newImages = [...images];
    const [removed] = newImages.splice(fromIndex, 1);
    newImages.splice(toIndex, 0, removed);
    onChange(newImages);
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-4">
      {/* Drop Zone */}
      <div
        onDragEnter={handleDragIn}
        onDragLeave={handleDragOut}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`
          relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer
          transition-all duration-200 ease-in-out
          ${isDragging 
            ? 'border-rm-primary bg-rm-primary/10 scale-[1.02]' 
            : 'border-slate-300 hover:border-rm-primary hover:bg-slate-50'
          }
          ${images.length >= maxImages ? 'opacity-50 pointer-events-none' : ''}
        `}
        data-testid="image-dropzone"
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={ALLOWED_TYPES.join(',')}
          multiple
          onChange={handleFileSelect}
          className="hidden"
          data-testid="image-input"
        />

        <AnimatePresence mode="wait">
          {isDragging ? (
            <motion.div
              key="dragging"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="flex flex-col items-center gap-3"
            >
              <div className="w-16 h-16 rounded-full bg-rm-primary/20 flex items-center justify-center">
                <Upload className="w-8 h-8 text-rm-primary animate-bounce" />
              </div>
              <p className="text-lg font-medium text-rm-primary">Déposez vos images ici</p>
            </motion.div>
          ) : uploading ? (
            <motion.div
              key="uploading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center gap-3"
            >
              <Loader2 className="w-12 h-12 text-rm-primary animate-spin" />
              <p className="text-rm-text-secondary">Compression et upload en cours...</p>
            </motion.div>
          ) : (
            <motion.div
              key="idle"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center gap-3"
            >
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center">
                <ImageIcon className="w-8 h-8 text-slate-400" />
              </div>
              <div>
                <p className="text-rm-text font-medium">
                  Glissez-déposez vos images ici
                </p>
                <p className="text-sm text-rm-text-secondary mt-1">
                  ou cliquez pour sélectionner
                </p>
              </div>
              <p className="text-xs text-slate-400">
                JPG, PNG, WebP • Max {MAX_FILE_SIZE / (1024 * 1024)} MB • {images.length}/{maxImages} images
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Upload Progress Indicators */}
        <AnimatePresence>
          {Object.entries(uploadProgress).map(([id, { status, progress, error }]) => (
            <motion.div
              key={id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className={`absolute bottom-2 left-2 right-2 px-3 py-2 rounded-lg text-sm flex items-center gap-2 ${
                status === 'error' ? 'bg-red-100 text-red-700' :
                status === 'done' ? 'bg-green-100 text-green-700' :
                'bg-blue-100 text-blue-700'
              }`}
            >
              {status === 'compressing' && (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Compression...
                </>
              )}
              {status === 'uploading' && (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Upload...
                </>
              )}
              {status === 'done' && (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Uploadé !
                </>
              )}
              {status === 'error' && (
                <>
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Image Grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
          {images.map((image, index) => (
            <motion.div
              key={image.id || index}
              layout
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className={`relative aspect-square rounded-xl overflow-hidden border-2 group ${
                image.is_primary ? 'border-rm-primary ring-2 ring-rm-primary/30' : 'border-slate-200'
              }`}
              data-testid={`image-preview-${index}`}
            >
              {/* Image */}
              <img 
                src={image.thumbnail_url || image.url} 
                alt={image.alt_text || `Image ${index + 1}`}
                className="w-full h-full object-cover"
              />

              {/* Primary Badge */}
              {image.is_primary && (
                <div className="absolute top-2 left-2 px-2 py-1 bg-rm-primary text-white text-xs font-medium rounded-full">
                  Principale
                </div>
              )}

              {/* Compression Info */}
              {image.originalSize && image.compressedSize && (
                <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/60 text-white text-xs rounded-full">
                  -{Math.round((1 - image.compressedSize / image.originalSize) * 100)}%
                </div>
              )}

              {/* Hover Overlay */}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                {!image.is_primary && (
                  <button
                    onClick={() => handleSetPrimary(index)}
                    className="p-2 bg-white rounded-full text-rm-text hover:bg-rm-primary hover:text-white transition-colors"
                    title="Définir comme principale"
                  >
                    <CheckCircle className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => handleRemove(index)}
                  className="p-2 bg-white rounded-full text-red-500 hover:bg-red-500 hover:text-white transition-colors"
                  title="Supprimer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Drag Handle (for future drag reorder) */}
              <div className="absolute top-2 right-2 p-1 bg-white/80 rounded opacity-0 group-hover:opacity-100 transition-opacity cursor-grab">
                <GripVertical className="w-4 h-4 text-slate-400" />
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Info */}
      {images.length > 0 && (
        <p className="text-xs text-slate-500 text-center">
          Cliquez sur une image pour la définir comme principale • Survolez pour supprimer
        </p>
      )}
    </div>
  );
}
